import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';
import { cn } from '@/nucleo/utilitarios';

export type TimelineStepStatus = 'completed' | 'current' | 'pending' | 'error';

export interface TimelineStep {
  sigla: string;
  label: string;
  status: TimelineStepStatus;
  responsavel?: string;
  slaLabel?: string;
  proximaAcao?: string;
}

const TOM: Record<TimelineStepStatus, { bg: string; text: string; ring?: string }> = {
  completed: { bg: 'bg-success', text: 'text-success-foreground' },
  current: {
    bg: 'bg-primary',
    text: 'text-primary-foreground',
    ring: 'ring-2 ring-primary/50 ring-offset-1 ring-offset-background',
  },
  pending: { bg: 'bg-muted', text: 'text-muted-foreground' },
  error: { bg: 'bg-destructive', text: 'text-destructive-foreground' },
};

/** Etapas do processo em siglas com tooltip — usado no cabeçalho do Fechamento
 * Mensal V2 (abertura → apontamento → validação → processamento → pagamento →
 * concluído). */
export function TimelineBadgeStepper({
  steps,
  onStepClick,
}: {
  steps: TimelineStep[];
  onStepClick?: () => void;
}) {
  if (steps.length === 0) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="flex flex-wrap items-center gap-1 py-1"
        role="list"
        aria-label="Etapas do processo"
      >
        {steps.map((step, i) => {
          const tom = TOM[step.status];
          const Elemento = onStepClick ? 'button' : 'span';
          return (
            <div key={step.sigla} className="flex items-center gap-1" role="listitem">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Elemento
                    type={onStepClick ? 'button' : undefined}
                    onClick={onStepClick}
                    className={cn(
                      'inline-flex h-6 min-w-[34px] items-center justify-center rounded-md px-2 text-[11px] font-bold uppercase tracking-wide transition-colors',
                      tom.bg,
                      tom.text,
                      tom.ring,
                      onStepClick && 'cursor-pointer hover:brightness-110',
                    )}
                    aria-current={step.status === 'current' ? 'step' : undefined}
                    aria-label={`${step.label}${step.status === 'current' ? ' (etapa atual)' : ''}`}
                  >
                    {step.sigla}
                  </Elemento>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <div className="max-w-[240px] space-y-0.5">
                    <p className="text-xs font-semibold">{step.label}</p>
                    {step.responsavel && (
                      <p className="text-[11px] opacity-90">Resp.: {step.responsavel}</p>
                    )}
                    {step.slaLabel && (
                      <p className="text-[11px] opacity-90">SLA: {step.slaLabel}</p>
                    )}
                    {step.proximaAcao && step.status === 'current' && (
                      <p className="mt-1 border-t border-border/40 pt-1 text-[11px] opacity-90">
                        → {step.proximaAcao}
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
              {i < steps.length - 1 && (
                <span className="h-0.5 w-2 rounded-full bg-border" aria-hidden="true" />
              )}
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
